/**
 * Error Handling Utilities
 * Provides specific error messages and error reporting functions
 */

/**
 * Get specific error message based on error type and context
 * @param {Error|string} error - The error object or message
 * @param {string} context - The context where the error occurred
 * @param {Object} additionalInfo - Additional information about the error
 * @returns {string} User-friendly error message
 */
export const getErrorMessage = (error, context = "operation", additionalInfo = {}) => {
  // Handle string errors
  if (typeof error === "string") {
    return `Error during ${context}: ${error}`;
  }

  // Handle Error objects
  if (error instanceof Error) {
    const errorMessage = error.message || error.toString();
    
    // Network errors
    if (errorMessage.includes("Network Error") || errorMessage.includes("timeout")) {
      return `Network error: Unable to connect to server. Please check your internet connection and try again.`;
    }

    // API errors with status codes
    if (error.response) {
      const status = error.response.status;
      const statusText = error.response.statusText;
      const errorData = error.response.data || {};
      
      // Handle ERROR_BAD_REQUEST format with error and details structure
      if (errorData.error === "ERROR_BAD_REQUEST" && errorData.details) {
        const details = errorData.details;
        // Extract specific validation errors if available
        if (details.detail && details.detail !== "Bad Request") {
          return details.detail;
        } else if (details.title && details.title !== "Bad request.") {
          return details.title;
        } else {
          // Provide context-specific messages based on operation
          if (context.includes("updating") || context.includes("update")) {
            return "Unable to update timer entry. Please verify your changes and try again.";
          } else if (context.includes("creating") || context.includes("create")) {
            return "Unable to create timer entry. Please check that all required fields are filled correctly and try again.";
          } else if (context.includes("deleting") || context.includes("delete")) {
            return "Unable to delete timer entry. The entry may be in use or you may not have permission.";
          } else {
            return "Invalid request. Please check your input and try again.";
          }
        }
      }
      
      switch (status) {
        case 400:
          return `Invalid request: ${errorData.message || statusText}. Please check your input and try again.`;
        case 401:
          return `Authentication failed: Your session may have expired. Please refresh the page and try again.`;
        case 403:
          return `Access denied: You don't have permission to perform this action.`;
        case 404:
          return `Resource not found: The requested ${context} could not be found.`;
        case 409:
          return `Conflict: ${errorData.message || "This action conflicts with the current state."}`;
        case 422:
          return `Validation error: ${errorData.message || "Please check your input data."}`;
        case 429:
          return `Too many requests: Please wait a moment and try again.`;
        case 500:
          return `Server error: An internal server error occurred. Please try again later or contact support.`;
        case 503:
          return `Service unavailable: The server is temporarily unavailable. Please try again later.`;
        default:
          return `Error ${status}: ${errorData.message || statusText || "An error occurred during " + context}`;
      }
    }

    // localStorage errors
    if (errorMessage.includes("localStorage") || errorMessage.includes("Storage")) {
      return `Storage error: Unable to save data locally. ${additionalInfo.action || "Please check your browser settings."}`;
    }

    // JSON parsing errors
    if (errorMessage.includes("JSON") || errorMessage.includes("parse")) {
      return `Data format error: Invalid data format encountered. ${additionalInfo.dataType || "Please refresh the page."}`;
    }

    // Time/Date errors
    if (errorMessage.includes("Invalid date") || errorMessage.includes("time")) {
      return `Time format error: Invalid time or date format. Please check your input.`;
    }

    // Default error message
    return `Error during ${context}: ${errorMessage}`;
  }

  // Fallback for unknown error types
  return `An unexpected error occurred during ${context}. Please try again.`;
};

/**
 * Log error with context and send to Slack if configured
 * @param {Error|string} error - The error to log
 * @param {string} context - The context where the error occurred
 * @param {Object} additionalInfo - Additional information
 */
export const handleError = (error, context = "operation", additionalInfo = {}) => {
  const errorMessage = getErrorMessage(error, context, additionalInfo);
  
  // Log to console with full error details
  console.error(`[${context}]`, {
    error: error instanceof Error ? error : new Error(error),
    message: errorMessage,
    additionalInfo,
    timestamp: new Date().toISOString(),
  });

  // Send to Slack if configured
  if (global.config?.slackMessage) {
    const slackMessage = `[Tracker Error] ${context}: ${errorMessage}\n${error instanceof Error ? error.stack : error}`;
    try {
      global.config.slackMessage(slackMessage);
    } catch (slackError) {
      console.error("Failed to send error to Slack:", slackError);
    }
  }

  return errorMessage;
};

/**
 * Create a toast error notification with specific message
 * @param {Error|string} error - The error
 * @param {string} context - The context
 * @param {Function} toastError - The toast.error function
 * @param {Object} options - Additional toast options
 */
export const showErrorToast = (error, context, toastError, options = {}) => {
  const errorMessage = getErrorMessage(error, context);
  toastError(errorMessage, {
    position: "bottom-right",
    autoClose: 5000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    ...options,
  });
};

/**
 * Handle API errors with specific messages
 * @param {Error} error - The API error
 * @param {string} operation - The operation being performed
 * @param {Function} toastError - The toast.error function
 * @returns {boolean} True if error was handled, false otherwise
 */
export const handleApiError = (error, operation, toastError) => {
  if (!error.response) {
    // Network error
    showErrorToast(error, `${operation} (network)`, toastError);
    return true;
  }

  const status = error.response.status;
  const errorData = error.response.data || {};
  
  // Handle ERROR_BAD_REQUEST format with error and details structure
  if (errorData.error === "ERROR_BAD_REQUEST" && errorData.details) {
    const details = errorData.details;
    let errorMessage = "Invalid request. Please check your input and try again.";
    
    // Extract specific validation errors if available
    if (details.detail && details.detail !== "Bad Request") {
      errorMessage = details.detail;
    } else if (details.title && details.title !== "Bad request.") {
      errorMessage = details.title;
    } else {
      // Provide context-specific messages based on operation
      if (operation.includes("updating") || operation.includes("update")) {
        errorMessage = "Unable to update timer entry. Please verify your changes and try again.";
      } else if (operation.includes("creating") || operation.includes("create")) {
        errorMessage = "Unable to create timer entry. Please check that all required fields are filled correctly and try again.";
      } else if (operation.includes("deleting") || operation.includes("delete")) {
        errorMessage = "Unable to delete timer entry. The entry may be in use or you may not have permission.";
      }
    }
    
    toastError(errorMessage, {
      position: "bottom-right",
      autoClose: 3500,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });
    return true;
  }

  const message = errorData.message;

  switch (status) {
    case 400:
      showErrorToast(
        message || `Invalid data provided for ${operation}`,
        operation,
        toastError
      );
      break;
    case 401:
      showErrorToast(
        "Your session has expired. Please refresh the page.",
        operation,
        toastError
      );
      break;
    case 403:
      showErrorToast(
        `You don't have permission to ${operation}`,
        operation,
        toastError
      );
      break;
    case 404:
      showErrorToast(
        `Resource not found for ${operation}`,
        operation,
        toastError
      );
      break;
    case 409:
      showErrorToast(
        message || `Conflict: Unable to ${operation} due to conflicting state`,
        operation,
        toastError
      );
      break;
    case 422:
      showErrorToast(
        message || `Validation failed for ${operation}`,
        operation,
        toastError
      );
      break;
    case 500:
    case 503:
      showErrorToast(
        `Server error during ${operation}. Please try again later.`,
        operation,
        toastError
      );
      break;
    default:
      showErrorToast(error, operation, toastError);
  }

  return true;
};
